import { Send, Upload } from 'lucide-react'
import { startOAuthOrReport } from '@/api/endpoints/oauth'
import { useEffect, useState, type FormEvent } from 'react'
import { toast } from 'sonner'

import { useStartTiktokUpload, useTiktokUploadStatus } from '@/api/endpoints/tiktok/uploads'
import { Button } from '@/components/ui/Button'
import { Chip } from '@/components/ui/Chip'
import { DialogClose, DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import { errorMessage } from '@/lib/errors'
import styles from './TiktokUploadModal.module.scss'

export interface TiktokUploadModalProps {
  open: boolean
  onClose: () => void
  /** The video about to be published. Title defaults from its metadata. */
  videoId: string
  language: string
  channel: string
  defaultTitle?: string
  defaultDescription?: string
  defaultTags?: ReadonlyArray<string>
  onStarted?: (jobKey: string) => void
}

const MODE_OPTIONS: SegmentedOption[] = [
  { value: 'inbox', label: 'Inbox (sandbox)' },
  { value: 'direct', label: 'Direct (prod)' },
]

const PRIVACY_OPTIONS: SegmentedOption[] = [
  { value: 'SELF_ONLY', label: 'Privado' },
  { value: 'MUTUAL_FOLLOW_FRIENDS', label: 'Amigos' },
  { value: 'PUBLIC_TO_EVERYONE', label: 'Público' },
]

/**
 * TikTok publish form. Mirrors the YouTube upload modal pattern but
 * trims the fields TikTok doesn't accept on its API (categories,
 * tags as a separate field — TikTok stuffs hashtags into the caption).
 *
 * In sandbox mode the only working ``mode`` is ``inbox`` — TikTok lands
 * the video as a draft inside the user's TikTok app instead of
 * publishing it. ``direct`` gets enabled once the app is approved
 * for production.
 */
export function TiktokUploadModal({
  open,
  onClose,
  videoId,
  language,
  channel,
  defaultTitle,
  defaultDescription,
  defaultTags,
  onStarted,
}: TiktokUploadModalProps) {
  const status = useTiktokUploadStatus(channel)
  const startMutation = useStartTiktokUpload()

  const [title, setTitle] = useState('')
  const [mode, setMode] = useState<'inbox' | 'direct'>('inbox')
  const [privacy, setPrivacy] = useState('SELF_ONLY')

  // Compose the default caption from the video's metadata. Title +
  // optional description + hashtags — TikTok caps captions at ~2200
  // chars so the backend trims, but we surface the full thing here so
  // the user can edit before submit.
  useEffect(() => {
    if (!open) return
    const baseTitle = defaultTitle ?? videoId
    const desc = defaultDescription ? `\n\n${defaultDescription}` : ''
    const tagLine = defaultTags?.length
      ? `\n\n${defaultTags.map((t) => (t.startsWith('#') ? t : `#${t}`)).join(' ')}`
      : ''
    setTitle(`${baseTitle}${desc}${tagLine}`.slice(0, 2200))
    setMode('inbox')
    setPrivacy('SELF_ONLY')
    startMutation.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, videoId, language, channel])

  const auth = status.data?.auth
  const connected = auth?.is_authenticated === true
  const configured = auth?.is_configured === true

  async function copyToClipboard(text: string): Promise<boolean> {
    // O `lib.dom` declara `Navigator.clipboard` como sempre presente, e por
    // isso o eslint chama a checagem de código morto. Ele está errado sobre o
    // runtime: a API só é exposta em contexto seguro (HTTPS ou localhost), e
    // em HTTP simples — como acessar o dev server por IP da LAN, que é o
    // caminho documentado no CLAUDE.md — ela vem `undefined` e o writeText
    // abaixo estouraria. A guarda fica.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!text || typeof navigator === 'undefined' || !navigator.clipboard) return false
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      return false
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!connected) return
    const captionText = title.trim()
    startMutation.mutate(
      {
        video_id: videoId,
        language,
        channel,
        mode,
        privacy,
        title: mode === 'inbox' ? null : captionText || null,
      },
      {
        onSuccess: async (data) => {
          if (mode === 'inbox') {
            const copied = await copyToClipboard(captionText)
            toast.success('Vídeo enviado pra sua inbox do TikTok', {
              description: copied
                ? 'Caption copiada — cola no app ao abrir o draft.'
                : 'Abra o draft no celular e digite a caption manualmente.',
            })
          } else {
            toast.success('Publicação iniciada no TikTok')
          }
          onStarted?.(data.job_key)
          onClose()
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Falha ao publicar no TikTok'))
        },
      },
    )
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={`Publicar no TikTok · ${channel}`}
        description={
          mode === 'inbox'
            ? 'Em sandbox, o vídeo vai como rascunho pra caixa de entrada do TikTok do seu perfil.'
            : 'Direct post — vídeo publica direto no feed. Disponível só após o app passar pelo review de produção.'
        }
        footer={
          <>
            <DialogClose asChild>
              <Button variant="secondary" disabled={startMutation.isPending}>
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="submit"
              form="tiktok-upload-form"
              leadingIcon={mode === 'inbox' ? <Upload /> : <Send />}
              disabled={startMutation.isPending || !connected}
            >
              {startMutation.isPending
                ? 'Enviando...'
                : mode === 'inbox'
                  ? 'Enviar pro Inbox'
                  : 'Publicar'}
            </Button>
          </>
        }
      >
        {!configured ? (
          <NotConfiguredCard />
        ) : !connected ? (
          <NotAuthenticatedCard channel={channel} />
        ) : (
          <form id="tiktok-upload-form" onSubmit={onSubmit} noValidate className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="tt-title" className={styles.label}>
                Caption <span className={styles.labelHint}>· até 2200 caracteres</span>
              </label>
              <textarea
                id="tt-title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value)
                }}
                rows={6}
                maxLength={2200}
                className={styles.textarea}
                placeholder="Texto que vai como caption do post"
              />
              <div className={styles.captionFooter}>
                {mode === 'inbox' ? (
                  <span className={styles.captionWarn}>
                    Em modo Inbox a API do TikTok não aceita caption — você digita no app ao abrir o draft.
                    Quando der "Enviar pro Inbox" a caption é copiada pro seu clipboard.
                  </span>
                ) : (
                  <span />
                )}
                <span className={styles.captionCount}>{title.length}/2200</span>
              </div>
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Modo</span>
              <Segmented
                value={mode}
                onValueChange={(v) => {
                  setMode(v as 'inbox' | 'direct')
                }}
                options={MODE_OPTIONS}
                aria-label="Modo de publicação"
              />
              <span className={styles.modeHint}>
                {mode === 'inbox'
                  ? 'Funciona em sandbox. Vídeo cai como rascunho no app TikTok.'
                  : 'Só após audit de produção. Vídeo publica direto no feed.'}
              </span>
            </div>

            {mode === 'direct' ? (
              <div className={styles.field}>
                <span className={styles.label}>Privacidade</span>
                <Segmented
                  value={privacy}
                  onValueChange={setPrivacy}
                  options={PRIVACY_OPTIONS}
                  aria-label="Privacidade"
                />
                <span className={styles.modeHint}>
                  Sandbox força <code>SELF_ONLY</code> independente do que escolher aqui.
                </span>
              </div>
            ) : null}
          </form>
        )}
      </DialogContent>
    </DialogRoot>
  )
}

function NotConfiguredCard() {
  return (
    <div className={styles.notConnected}>
      <Chip variant="warning">Sem TIKTOK_CLIENT_KEY</Chip>
      <p className={styles.notConnectedDesc}>
        Preencha <code>TIKTOK_CLIENT_KEY</code> e <code>TIKTOK_CLIENT_SECRET</code> no <code>.env</code> e
        recrie o backend.
      </p>
    </div>
  )
}

function NotAuthenticatedCard({ channel }: { channel: string }) {
  return (
    <div className={styles.notConnected}>
      <p className={styles.notConnectedDesc}>
        O perfil <strong>{channel}</strong> ainda não está conectado ao TikTok.
      </p>
      <Button
        variant="primary"
        onClick={() => {
          startOAuthOrReport('tiktok', channel, (m) => toast.error(m))
        }}
      >
        Conectar TikTok
      </Button>
    </div>
  )
}
